import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {GRAY, GRAY_DARK, RED_LIGHT} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import SelectTree from 'ui-library/select-tree/SelectTree';
import pick from 'util/pick';

import {LookupStepInfo, LookupStepTreeItem, LookupStepTreeItemsById} from './LookupConfigModalConstants';
import LookupStepChip from './LookupStepChip';
import {
  allowSchemaFieldSteps,
  getAllParentSteps,
  getLookupStepTreeItemId,
  getSchemaFieldSelectTreeItems,
  hasFollowingSchemaFieldSteps,
} from './SchemaFieldSelectTreeUtils';

interface SelectedItemDisplayProps {
  disabled: boolean;
  hasError?: boolean;
  lookupStepInfo: LookupStepInfo;
  label?: string;
}

function SelectedItemDisplay(props: SelectedItemDisplayProps): JSX.Element | null {
  return (
    <>
      <FieldLabel label={props.label || 'Linked item'} labelStyle={LabelStyle.LABEL} />
      <div
        className={css(
          styles.selectedItemInput,
          props.hasError && styles.selectedItemInputHasError,
          props.disabled && styles.selectedItemInputDisabled
        )}
      >
        <LookupStepChip
          lookupStepInfo={props.lookupStepInfo}
          hasError={props.hasError}
          shouldFadeChip={props.disabled}
        />
      </div>
    </>
  );
}

export interface SchemaFieldSelectTreeProps {
  disabled: boolean;
  hasError?: boolean;
  lookupStepGroup: Array<LookupStepInfo>;
  handleSetLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  depth: number;
  label?: string;
  shouldAlwaysShowSelectedItem?: boolean;
  schemaFieldTreeFilterFn?: (field: SchemaField) => boolean;
  selectTreeItemsById: LookupStepTreeItemsById;
  setSelectTreeItemsById: (items: LookupStepTreeItemsById) => void;
  typecastSchemasByNodeId: Map<string, Schema | null>;
  updateTypecastSchemasByNodeId: (nodeId: string, typecastSchema: Schema | null) => void;
}

export default function SchemaFieldSelectTree(props: SchemaFieldSelectTreeProps): JSX.Element | null {
  const {
    selectTreeItemsById,
    setSelectTreeItemsById,
    typecastSchemasByNodeId,
    updateTypecastSchemasByNodeId,
  } = props;

  function isLoadedItemExpandable(item: LookupStepTreeItem): boolean {
    const hasFollowingSteps = hasFollowingSchemaFieldSteps(item, props.schemaFieldTreeFilterFn);
    return hasFollowingSteps && item.depth < props.depth;
  }

  function loadMore(
    parentId: string,
    numItemsAlreadyLoaded: number
  ): Promise<{items: Array<LookupStepTreeItem>; offset: number; more: boolean}> {
    return new Promise((resolve) => {
      const parentItem = selectTreeItemsById[parentId];
      // We either load all of the items or none of the items. Therefore, if the number of items already loaded
      // is nonzero, we can expect that we have already fetched all of the items for the given parent, and can
      // optimize by skipping the fetch step.
      if (numItemsAlreadyLoaded !== 0 || parentItem.depth > props.depth) {
        resolve({items: [], offset: numItemsAlreadyLoaded, more: false});
      }
      const {[parentId]: LookupStepTreeItem, ...fields} = getSchemaFieldSelectTreeItems(
        parentItem,
        props.schemaFieldTreeFilterFn,
        1,
        props.depth,
        typecastSchemasByNodeId,
        updateTypecastSchemasByNodeId,
        initialSchemaFieldToTypecast,
        parentId !== 'ROOT' ? parentId : undefined,
        parentItem.depth
      );
      setSelectTreeItemsById({...selectTreeItemsById, ...fields});
      resolve({
        items: Object.values(fields),
        offset: numItemsAlreadyLoaded,
        more: false,
      });
    });
  }

  const {handleSetLookupStepGroup, lookupStepGroup, depth, schemaFieldTreeFilterFn} = props;
  const [shouldAllowSchemaFieldSteps, setShouldAllowSchemaFieldSteps] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  // If we are generating a select tree where the existing lookup steps contain typecast information, we need
  // the typecasting data in a different format as the tree node IDs (the keys of typecastSchemasByNodeId) do not yet exist.
  const initialSchemaFieldToTypecast: Map<string, Schema | null> = lookupStepGroup.reduce(
    (accumulator, lookupStepInfo) => {
      const typecastSchema = lookupStepInfo.typecastSchema;
      const schemaField = lookupStepInfo.schemaField;
      if (schemaField && typecastSchema) {
        accumulator.set(schemaField.apiId, typecastSchema);
      }
      return accumulator;
    },
    new Map<string, Schema | null>()
  );

  const firstLookupStep = lookupStepGroup[0];
  const prevFirstLookupStep = usePrevious(firstLookupStep);
  const prevTypecastSchemasByNodeId = usePrevious(typecastSchemasByNodeId);

  const treeItemToInfoObject = (treeItem: LookupStepTreeItem): LookupStepInfo =>
    pick(
      treeItem,
      'name',
      'lookupStep',
      'lookupStepContext',
      'schemaField',
      'parentSchema',
      'linkSchema',
      'originalLinkSchema',
      'typecastSchema'
    );

  const recalculateLookupStepGroupFromParent = useCallback(
    (selectedNodeId: string, newLookupTree: LookupStepTreeItemsById): void => {
      const parentItem = newLookupTree[selectedNodeId];
      const lookupStepInfos: Array<LookupStepInfo> = getAllParentSteps(parentItem, newLookupTree).map(
        (treeItem) => treeItemToInfoObject(treeItem)
      );
      handleSetLookupStepGroup([...lookupStepInfos.reverse()]);
    },
    [handleSetLookupStepGroup]
  );

  const onChangeSelectedIds = useCallback(
    (selectedIds: Array<string>): void => {
      assert(selectedIds.length === 1, 'Expected 1 selected step');
      if (selectedIds[0] === selectedId) {
        return;
      }
      recalculateLookupStepGroupFromParent(selectedIds[0], selectTreeItemsById);
    },
    [selectTreeItemsById, selectedId, recalculateLookupStepGroupFromParent]
  );

  // Get the differing node ID between two typecastMap objects. Assumes there is at most one differing node
  // and that current is a superset of previous, as keys can only be added or modified to the map.
  const getDifferingNodeId = (
    previous: Map<string, Schema | null> | undefined,
    current: Map<string, Schema | null>
  ): string | null => {
    let differingKey: string | null = null;
    current.forEach((value, key) => {
      if (!previous || !previous.has(key) || previous.get(key) !== value) {
        differingKey = key;
      }
    });
    return differingKey;
  };

  const filterOutNodeAndChildren = (
    treeItemsToFilter: LookupStepTreeItemsById,
    nodeId: string
  ): LookupStepTreeItemsById =>
    Object.entries(treeItemsToFilter).reduce((accumulator: LookupStepTreeItemsById, [key, value]) => {
      if (nodeId !== 'ROOT' && !key.startsWith(nodeId)) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});

  // If a typecast selection was made, update the select tree with new node information.
  // Additionally, we may need to update the selectedNode, if it no longer exists due to the typecast change.
  useEffect(() => {
    const nodeId = getDifferingNodeId(prevTypecastSchemasByNodeId, typecastSchemasByNodeId);
    if (nodeId == null) {
      return;
    }

    const parentItem = selectTreeItemsById[nodeId];
    const {[nodeId]: newVal, ...fields} = getSchemaFieldSelectTreeItems(
      parentItem,
      props.schemaFieldTreeFilterFn,
      1,
      parentItem.depth + 1,
      typecastSchemasByNodeId,
      updateTypecastSchemasByNodeId,
      initialSchemaFieldToTypecast,
      nodeId !== 'ROOT' ? nodeId : undefined,
      parentItem.depth
    );
    // Splice in the typecasted node to its corrected parent
    newVal.parentId = parentItem.parentId;

    // Splice in the typecasted node's children and remove the previous children
    const filteredSelectTreeItemsById = filterOutNodeAndChildren(selectTreeItemsById, nodeId);
    const newSelectTreeItemsById = {[nodeId]: newVal, ...filteredSelectTreeItemsById, ...fields};
    setSelectTreeItemsById(newSelectTreeItemsById);

    if (nodeId === 'ROOT' || selectedId.startsWith(nodeId)) {
      // If our previous selected ID is a now-orphaned child of the node we typecasted, set the selected ID to the parent.
      setSelectedId(nodeId);
      onChangeSelectedIds([nodeId]);

      // Update lookup step group in case we typecasted a node in the path.
      recalculateLookupStepGroupFromParent(nodeId, newSelectTreeItemsById);
    }
  }, [
    handleSetLookupStepGroup,
    lookupStepGroup,
    onChangeSelectedIds,
    prevTypecastSchemasByNodeId,
    props.schemaFieldTreeFilterFn,
    typecastSchemasByNodeId,
    selectTreeItemsById,
    selectedId,
    initialSchemaFieldToTypecast,
    recalculateLookupStepGroupFromParent,
    updateTypecastSchemasByNodeId,
    setSelectTreeItemsById,
  ]);

  useEffect(() => {
    const isFirstIteration = prevFirstLookupStep === undefined && firstLookupStep !== undefined;
    if (!isFirstIteration && !isEqual(prevFirstLookupStep?.lookupStep, firstLookupStep?.lookupStep)) {
      setSelectTreeItemsById({});
      setShouldAllowSchemaFieldSteps(false);
      setSelectedId('');
      return;
    }

    const schemaFieldSteps = lookupStepGroup.slice(1);
    setSelectedId(
      schemaFieldSteps.length
        ? schemaFieldSteps
            .map((lookupStepInfo) => getLookupStepTreeItemId(lookupStepInfo.lookupStep))
            .join('-')
        : 'ROOT'
    );

    const canHaveSteps = allowSchemaFieldSteps(lookupStepGroup, schemaFieldTreeFilterFn);
    if (canHaveSteps && !Object.keys(selectTreeItemsById).length) {
      setSelectTreeItemsById(
        getSchemaFieldSelectTreeItems(
          lookupStepGroup[0],
          schemaFieldTreeFilterFn,
          Math.max(1, schemaFieldSteps.length),
          depth,
          typecastSchemasByNodeId,
          updateTypecastSchemasByNodeId,
          initialSchemaFieldToTypecast
        )
      );
    }
    setShouldAllowSchemaFieldSteps(canHaveSteps);
  }, [
    lookupStepGroup,
    depth,
    selectTreeItemsById,
    prevFirstLookupStep,
    firstLookupStep,
    schemaFieldTreeFilterFn,
    typecastSchemasByNodeId,
    prevTypecastSchemasByNodeId,
    initialSchemaFieldToTypecast,
    setSelectTreeItemsById,
    updateTypecastSchemasByNodeId,
  ]);

  if (!shouldAllowSchemaFieldSteps) {
    return props.shouldAlwaysShowSelectedItem && props.lookupStepGroup.length ? (
      <SelectedItemDisplay
        disabled={props.disabled}
        hasError={props.hasError}
        lookupStepInfo={props.lookupStepGroup[props.lookupStepGroup.length - 1]}
        label={props.label}
      />
    ) : null;
  }

  return (
    <>
      <SelectedItemDisplay
        disabled={props.disabled}
        hasError={props.hasError}
        lookupStepInfo={props.lookupStepGroup[props.lookupStepGroup.length - 1]}
        label={props.label}
      />
      <SelectTree
        disabled={props.disabled}
        baseLeafItems={[]}
        items={Object.values(selectTreeItemsById)}
        itemTypeName="links"
        multiple={false}
        selectedIds={[selectedId]}
        onChangeSelectedIds={(selectedIds) => {
          onChangeSelectedIds(selectedIds);
        }}
        shouldExpandNewItems={false}
        shouldExpandOnSelect={true}
        isLoadedItemExpandable={isLoadedItemExpandable}
        loadMore={(parentId, numItemsAlreadyLoaded) => loadMore(parentId, numItemsAlreadyLoaded)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  selectedItemInput: {
    border: `1px solid ${GRAY}`,
    borderRadius: '3px',
    padding: '2px',
    width: '100%',
  },
  selectedItemInputDisabled: {
    textDecoration: 'none',
    color: GRAY_DARK,
    cursor: 'not-allowed',
  },
  selectedItemInputHasError: {
    border: `1px solid ${RED_LIGHT}`,
  },
});
